import React, { useEffect, useState } from 'react';
import { PDFTemplateService, PDFTemplate, CreateTemplatePayload, UpdateTemplatePayload } from '../services/api/pdfTemplate.service';
import { CompanyService } from '../services/api/company.service';
import { Company } from '../types';
import Modal from './Modal';

const pdfTemplateService = new PDFTemplateService();
const companyService = new CompanyService();

type FormMode = 'create' | 'edit';

interface TemplateFormProps {
  mode: FormMode;
  initialData?: PDFTemplate;
  onSave: (values: CreateTemplatePayload | UpdateTemplatePayload) => void;
  onCancel: () => void;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ mode, initialData, onSave, onCancel }) => {
  const [formData, setFormData] = useState<CreateTemplatePayload | UpdateTemplatePayload>(() => {
    if (initialData) {
      const { id, template_id, created_at, updated_at, ...rest } = initialData;
      return {
        ...rest,
        updated_by: 'system',
      } as UpdateTemplatePayload;
    }
    return {
      template_name: '',
      document_type: 'invoice',
      language: 'en',
      template_path: '/app/templates/pdf',
      template_filename: 'invoice_en.html',
      created_by: 'system',
    } as CreateTemplatePayload;
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    setFormData((prev) => ({
      ...prev,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="template-form space-y-4">
      <div>
        <label className="block mb-1 font-medium">Template Name *</label>
        <input
          name="template_name"
          value={(formData as any).template_name || ''}
          onChange={handleChange}
          className="w-full border px-3 py-2 rounded"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 font-medium">Document Type *</label>
          <select
            name="document_type"
            value={(formData as any).document_type}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded"
            required
          >
            <option value="invoice">Invoice</option>
            <option value="invoice_draft">Invoice Draft</option>
            <option value="payment_confirmation">Payment Confirmation</option>
            <option value="reminder_no_fee">Reminder (No Fee)</option>
            <option value="reminder_with_fee">Reminder (With Fee)</option>
          </select>
        </div>

        <div>
          <label className="block mb-1 font-medium">Language *</label>
          <select
            name="language"
            value={(formData as any).language}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded"
            required
          >
            <option value="de">DE</option>
            <option value="en">EN</option>
            <option value="pt-br">PT-BR</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 font-medium">Template Path *</label>
          <input
            name="template_path"
            value={(formData as any).template_path}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded"
            required
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Template Filename *</label>
          <input
            name="template_filename"
            value={(formData as any).template_filename}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded"
            required
          />
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-1">
          <input
            type="checkbox"
            name="is_default"
            checked={(formData as any).is_default || false}
            onChange={handleChange}
          />
          <span>Default</span>
        </label>
        <label className="flex items-center space-x-1">
          <input
            type="checkbox"
            name="is_active"
            checked={(formData as any).is_active ?? true}
            onChange={handleChange}
          />
          <span>Active</span>
        </label>
      </div>

      <div className="text-right space-x-2">
        <button type="button" className="btn-secondary px-4 py-2" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary px-4 py-2">
          {mode === 'create' ? 'Create' : 'Update'}
        </button>
      </div>
    </form>
  );
};

interface ImportDialogProps {
  onImport: (file: File | null, companyId?: number, overrideExisting?: boolean) => void;
  onCancel: () => void;
}

const ImportDialog: React.FC<ImportDialogProps> = ({ onImport, onCancel }) => {
  const [importType, setImportType] = useState<'file' | 'default'>('file');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [companyId, setCompanyId] = useState<number | undefined>(undefined);
  const [overrideExisting, setOverrideExisting] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const companiesData = await companyService.getCompanies();
        setCompanies(companiesData);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (importType === 'file' && !selectedFile) {
      alert('Please select a file to import');
      return;
    }
    onImport(importType === 'file' ? selectedFile : null, companyId, overrideExisting);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block mb-2 font-medium">Import Type</label>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              value="file"
              checked={importType === 'file'}
              onChange={(e) => setImportType(e.target.value as 'file' | 'default')}
            />
            <span>Import from JSON file</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              value="default"
              checked={importType === 'default'}
              onChange={(e) => setImportType(e.target.value as 'file' | 'default')}
            />
            <span>Import default templates</span>
          </label>
        </div>
      </div>

      {importType === 'file' && (
        <div>
          <label className="block mb-1 font-medium">Select JSON File *</label>
          <input
            type="file"
            accept=".json"
            onChange={handleFileChange}
            className="w-full border px-3 py-2 rounded"
            required
          />
          {selectedFile && (
            <p className="text-sm text-gray-600 mt-1">Selected: {selectedFile.name}</p>
          )}
        </div>
      )}

      <div>
        <label className="block mb-1 font-medium">Company (Optional)</label>
        <select
          value={companyId || ''}
          onChange={(e) => setCompanyId(e.target.value ? parseInt(e.target.value) : undefined)}
          className="w-full border px-3 py-2 rounded"
          disabled={loadingCompanies}
        >
          <option value="">Global Templates (All Companies)</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>
        <p className="text-sm text-gray-600 mt-1">
          Leave empty to import as global templates available to all companies
        </p>
      </div>

      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={overrideExisting}
            onChange={(e) => setOverrideExisting(e.target.checked)}
          />
          <span>Override existing templates</span>
        </label>
        <p className="text-sm text-gray-600 mt-1">
          If checked, existing templates with the same name will be updated
        </p>
      </div>

      <div className="text-right space-x-2">
        <button type="button" className="btn-secondary px-4 py-2" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary px-4 py-2">
          Import
        </button>
      </div>
    </form>
  );
};

const PDFTemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<PDFTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<FormMode>('create');
  const [selectedTemplate, setSelectedTemplate] = useState<PDFTemplate | undefined>();
  const [showImportDialog, setShowImportDialog] = useState(false);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await pdfTemplateService.listTemplates();
      setTemplates(res.templates);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const openCreate = () => {
    setModalMode('create');
    setSelectedTemplate(undefined);
    setShowModal(true);
  };

  const openEdit = (template: PDFTemplate) => {
    setModalMode('edit');
    setSelectedTemplate(template);
    setShowModal(true);
  };

  const handleUpload = async (templateId: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.html,.htm,.jinja,.jinja2,.j2';
    input.onchange = async () => {
      if (input.files && input.files[0]) {
        try {
          await pdfTemplateService.uploadTemplateFile(templateId, input.files[0]);
          alert('File uploaded successfully');
        } catch (err: any) {
          alert(err.message || 'Upload failed');
        }
      }
    };
    input.click();
  };

  const handleSave = async (values: CreateTemplatePayload | UpdateTemplatePayload) => {
    try {
      if (modalMode === 'create') {
        await pdfTemplateService.createTemplate(values as CreateTemplatePayload);
      } else if (selectedTemplate) {
        await pdfTemplateService.updateTemplate(selectedTemplate.id, values as UpdateTemplatePayload);
      }
      await fetchTemplates();
      setShowModal(false);
    } catch (err: any) {
      alert(err.message || 'Save failed');
    }
  };

  const handleDelete = async (templateId: number) => {
    if (!templateId) {
      console.error('Template ID is undefined');
      alert('Error: Template ID is missing');
      return;
    }
    if (!window.confirm('Delete this template?')) return;
    try {
      await pdfTemplateService.deleteTemplate(templateId);
      await fetchTemplates();
    } catch (err: any) {
      console.error('Delete error:', err);
      alert(err.message || 'Delete failed');
    }
  };

  const handleImport = async (file: File | null, companyId?: number, overrideExisting?: boolean) => {
    try {
      let result;
      if (file) {
        // Import from file
        result = await pdfTemplateService.importTemplates(file, companyId, overrideExisting);
        
        // Show detailed results
        let message = result.message + '\n\n';
        
        if (result.results.imported.length > 0) {
          message += 'Imported templates:\n';
          result.results.imported.forEach(item => {
            message += `- ${item.template_name} (${item.action})\n`;
          });
        }
        
        if (result.results.skipped.length > 0) {
          message += '\nSkipped templates:\n';
          result.results.skipped.forEach(item => {
            message += `- ${item.template_name}: ${item.reason}\n`;
          });
        }
        
        if (result.results.errors.length > 0) {
          message += '\nErrors:\n';
          result.results.errors.forEach(item => {
            message += `- ${item.template_name}: ${item.error}\n`;
          });
        }
        
        alert(message);
      } else {
        // Import default templates
        result = await pdfTemplateService.importDefaultTemplates(companyId, overrideExisting);
        alert(result.message);
      }
      
      await fetchTemplates();
      setShowImportDialog(false);
    } catch (err: any) {
      alert(err.message || 'Import failed');
    }
  };

  const handleExport = async () => {
    try {
      const includeContent = window.confirm('Include template HTML content in export?');
      const templates = await pdfTemplateService.exportTemplates(undefined, includeContent);
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(templates, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `templates_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Export failed');
    }
  };

  return (
    <div className="p-6 bg-white rounded shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">PDF Templates</h2>
        <div className="space-x-2">
          <button className="btn-secondary px-4 py-2" onClick={() => setShowImportDialog(true)}>
            Import
          </button>
          <button className="btn-secondary px-4 py-2" onClick={handleExport}>
            Export
          </button>
          <button className="btn-primary px-4 py-2" onClick={openCreate}>
            New Template
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <table className="w-full border text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-2 py-1">Name</th>
              <th className="border px-2 py-1">Type</th>
              <th className="border px-2 py-1">Lang</th>
              <th className="border px-2 py-1">Default</th>
              <th className="border px-2 py-1">Active</th>
              <th className="border px-2 py-1">Version</th>
              <th className="border px-2 py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => {
              console.log('Template:', t); // Debug log
              return (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="border px-2 py-1">{t.template_name}</td>
                <td className="border px-2 py-1">{t.document_type}</td>
                <td className="border px-2 py-1">{t.language}</td>
                <td className="border px-2 py-1 text-center">{t.is_default ? '✓' : ''}</td>
                <td className="border px-2 py-1 text-center">{t.is_active ? '✓' : ''}</td>
                <td className="border px-2 py-1">{t.version}</td>
                <td className="border px-2 py-1 space-x-2 text-center">
                  <button className="text-blue-600" onClick={() => openEdit(t)}>
                    Edit
                  </button>
                  <button className="text-green-600" onClick={() => handleUpload(t.id)}>
                    Upload
                  </button>
                  <button className="text-red-600" onClick={() => handleDelete(t.id)}>
                    Delete
                  </button>
                </td>
              </tr>
              );
            })}
            {templates.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-4">
                  No templates found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {showModal && (
        <Modal title={modalMode === 'create' ? 'Create Template' : 'Edit Template'} onClose={() => setShowModal(false)}>
          <TemplateForm
            mode={modalMode}
            initialData={selectedTemplate}
            onSave={handleSave}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      )}

      {showImportDialog && (
        <Modal title="Import Templates" onClose={() => setShowImportDialog(false)}>
          <ImportDialog
            onImport={handleImport}
            onCancel={() => setShowImportDialog(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default PDFTemplateManager;
